import { useMutation, useQuery } from "@tanstack/react-query";
import { useAlert } from "context/alert";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  createPatientDocumentUpload,
  fetchDowloadDocument,
  fetchResultEntryDowloadDocument,
  deleteResultEntryDowloadDocument,
} from "services/resultEntryReportMaster";
import { STALE_TIME } from "utils/constants";
import zoneLocalStorageId from "utils/zone";

const UploadDocument = ({
  onClose,
  rowData,
  setdocumentInfo,
  setDocumentModal,
  setIsOpenObservationValue,
  openedFrom,
}) => {
  const { showAlert } = useAlert();
  const [files, setFiles] = useState([]);
  const [fileRequired, setFileRequired] = useState(false);
  const jwtAuthreducer = useSelector((state) => state.jwtAuthreducer);
  const _token =
    jwtAuthreducer?.token_values?.sub &&
    JSON.parse(jwtAuthreducer?.token_values?.sub?.replace(/'/g, '"'));

  const [filePath, setFilePath] = useState("");
  const [downloadedFiles, setDownloadedFiles] = useState([]);

  const { data, refetch: refetchDocuments } = useQuery(
    [
      "fetch-result-entry-download-documents",
      rowData?.appointment_id,
      rowData?.investigation_data?.investigation_id,
    ],
    () =>
      fetchResultEntryDowloadDocument({
        appointment_id: rowData?.appointment_id,
        investigation_id: rowData?.investigation_data?.investigation_id,
      }),
    {
      staleTime: STALE_TIME,
      enabled:
        !!rowData?.appointment_id ||
        !!rowData?.investigation_data?.investigation_id,
    }
  );

  const onSuccessDelete = () => {
    const refetchProducts = true;
    const message = "Document delete successfully!";
    showAlert(message, "success");
    refetchDocuments();
    // onClose(refetchDocuments);
  };

  const onErrorDelete = useCallback((error) => {
    const errorMessage =
      error?.response?.data?.message || "Something went wrong!";
    const errorDetail = error?.response?.data?.detail;
    showAlert(errorMessage, "danger", errorDetail);
  }, []);

  const { mutate: deleteResultEntryDowloadDocumentMutate } = useMutation({
    mutationFn: deleteResultEntryDowloadDocument,
    onSuccess: onSuccessDelete,
    onError: onErrorDelete,
  });

  console.log("downloadedFiles", downloadedFiles);

  useEffect(() => {
    if (data?.data?.data?.length) {
      setDownloadedFiles(data.data.data);
    }
  }, [data]);

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (files.length + selectedFiles.length > 3) {
      showAlert("Maximum 3 files can be uploaded", "warning");
      return;
    }

    const processedFiles = await Promise.all(
      selectedFiles.map(async (file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const base64String = reader.result;
            const base64Data = base64String.split(",")[1];

            resolve({
              file: file,
              fileName: file.name,
              fileData: base64Data,
              fileType: file.type,
              previewUrl: base64String,
              fileSize: file.size,
            });
          };
          reader.onerror = (error) => {
            console.error("Error converting file to base64:", error);
            resolve(null);
          };
        });
      })
    );

    setFiles([...files, ...processedFiles.filter((file) => file !== null)]);
    setFilePath(
      files.length > 0 ? "multiple_files" : selectedFiles[0]?.name || ""
    );
    setFileRequired(files.length + selectedFiles.length === 0);
  };

  const removeFile = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
    setFileRequired(updatedFiles.length === 0);
  };

  const onSuccess = (res) => {
    showAlert("Documents uploaded successfully!");
    setdocumentInfo(res);
    setDocumentModal(false);
    // setIsOpenObservationValue(true);
    if (openedFrom === "uploadAttachment") {
      setIsOpenObservationValue(false);
    } else {
      setIsOpenObservationValue(true);
    }
  };

  const onError = (res) => {
    console.log(res);
    showAlert("Error uploading documents", "error");
  };

  const { mutate: addDocumentMutate } = useMutation({
    mutationFn: createPatientDocumentUpload,
    onSuccess,
    onError,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (files.length === 0) {
      setFileRequired(true);
      return;
    }

    const fileDataArray = files.map((fileObj) => ({
      file_name: fileObj.fileName,
      file_data: fileObj.fileData,
    }));

    const payload = {
      patient_id: rowData?.patient_id,
      appointment_id: rowData?.appointment_id,
      investigation_id: rowData?.investigation_data?.investigation_id,
      file_data: fileDataArray,
      file_path: filePath || "multiple_files",
      franchise_id: rowData?.franchise_id || _token?.franchise_id,
      zone_id: rowData?.zone_id || zoneLocalStorageId(),
      is_active: true,
      created_by: jwtAuthreducer.user.employee_code_id,
    };

    if (payload) {
      addDocumentMutate(payload);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isImageFile = (fileType) => {
    return fileType && fileType.startsWith("image/");
  };

  const isPdfFile = (fileType) => {
    return fileType === "application/pdf";
  };

  const renderFilePreview = (fileObj) => {
    if (isImageFile(fileObj.fileType)) {
      return (
        <img
          src={fileObj.previewUrl}
          alt={fileObj.fileName}
          className="img-thumbnail"
          style={{ maxWidth: "200px", maxHeight: "200px", objectFit: "cover" }}
        />
      );
    } else if (isPdfFile(fileObj.fileType)) {
      return (
        <div
          className="d-flex align-items-center justify-content-center border rounded"
          style={{
            width: "100px",
            height: "100px",
            backgroundColor: "#f8f9fa",
          }}
        >
          <i
            className="fas fa-file-pdf text-danger"
            style={{ fontSize: "2rem" }}
          ></i>
        </div>
      );
    } else {
      return (
        <div
          className="d-flex align-items-center justify-content-center border rounded"
          style={{
            width: "100px",
            height: "100px",
            backgroundColor: "#f8f9fa",
          }}
        >
          <i
            className="fas fa-file text-secondary"
            style={{ fontSize: "2rem" }}
          ></i>
        </div>
      );
    }
  };

  const removeDownloadedFile = (id, indexToRemove) => {
    deleteResultEntryDowloadDocumentMutate(id);
    setDownloadedFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mt-3">
        <label className="form-label">
          Upload Document <span className="alertColor">*</span>
          {files.length > 0 && (
            <span className="ms-2 text-muted">({files.length}/3 files)</span>
          )}
        </label>
        <input
          type="file"
          className={`form-control ${fileRequired ? "is-invalid" : ""}`}
          onChange={handleFileChange}
          accept=".pdf,image/*"
          multiple
          disabled={files.length >= 3}
        />
        {fileRequired && <div className="text-danger">File is required</div>}
        {files.length >= 3 && (
          <div className="text-warning mt-1">
            Maximum file limit reached (3)
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div className="mt-3">
          <label className="form-label">Selected Files with Preview:</label>
          <div className="row">
            {files.map((fileObj, index) => (
              <div key={index} className="col-md-6 col-lg-4 mb-3">
                <div className="card h-100 shadow-sm">
                  <div className="card-body p-3">
                    <div className="text-center mb-3">
                      {renderFilePreview(fileObj)}
                    </div>

                    <div className="mb-3">
                      <div
                        className="text-truncate fw-bold mb-1"
                        style={{ maxWidth: "100%" }}
                        title={fileObj.fileName}
                      >
                        {fileObj.fileName}
                      </div>
                      <small className="text-muted">
                        {formatFileSize(fileObj.fileSize)}
                      </small>
                    </div>

                    <div className="d-grid">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeFile(index)}
                      >
                        <i className="fas fa-trash me-1"></i> Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {downloadedFiles.length > 0 && (
        <div className="mt-4">
          <strong className="mb-3 d-block">Already Uploaded Documents</strong>
          <div className="d-flex flex-wrap justify-content-between gap-3">
            {downloadedFiles.map((file, index) => {
              const { id, file_name, file_data } = file;
              const extension = file_name.split(".").pop()?.toLowerCase();
              const mimeType =
                extension === "pdf"
                  ? "application/pdf"
                  : extension === "jpg" || extension === "jpeg"
                  ? "image/jpeg"
                  : extension === "png"
                  ? "image/png"
                  : "application/octet-stream";

              const base64Url = `data:${mimeType};base64,${file_data}`;

              return (
                <div
                  key={id || index}
                  className="border p-3 rounded shadow-sm d-flex flex-column justify-content-between"
                  style={{ width: "48%" }}
                >
                  <div>
                    <p className="fw-bold mb-2 text-truncate" title={file_name}>
                      {file_name}
                    </p>
                    {mimeType.startsWith("image/") ? (
                      <img
                        src={base64Url}
                        alt={file_name}
                        style={{ maxWidth: "100%", height: "auto" }}
                      />
                    ) : (
                      <a
                        href={base64Url}
                        download={file_name}
                        className="btn btn-outline-primary btn-sm mb-2"
                      >
                        Download
                      </a>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm mt-2"
                    onClick={() => removeDownloadedFile(id, index)}
                  >
                    <i className="fas fa-trash me-1"></i> Remove
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 d-flex justify-content-start">
        <button type="submit" className="btn btn-primary">
          Upload
        </button>
      </div>
    </form>
  );
};

export default UploadDocument;
